import { useCallback, useEffect, useState } from 'react';

/**
 * Ein Router in dreissig Zeilen.
 *
 * **Warum keine Bibliothek.** Die Anwendung hat vier Adressen. Ein Router
 * dafür brächte Verschachtelung, Lader, Aktionen und ein eigenes
 * Datenmodell mit — und wäre die grösste Abhängigkeit des Frontends. Was hier
 * gebraucht wird, ist der Pfad, ein `pushState` und ein Zuhörer auf
 * `popstate`; alles drei steht in der Plattform.
 *
 * **Tiefe Adressen sind Absicht.** Eine Buchung hat unter `/journal/<id>`
 * eine eigene Adresse, die sich weitergeben und neu laden lässt. Der
 * Auslieferungsserver liefert dafür `index.html` (siehe `web-nginx.conf`),
 * der Entwicklungsserver von Vite tut dasselbe.
 */

/** Die aufgelöste Adresse. */
export type Route =
  // **Steht als Erstes, nicht weil sie die Startadresse wäre — `DA-4` ist
  // noch offen, und der Rückfall auf eine unbekannte Adresse bleibt das
  // Journal, siehe unten in `toRoute` — sondern weil sie im Menü an erster
  // Stelle steht (Spec 6.2, Task 13).**
  | { readonly name: 'overview' }
  // Die Such**seite** (Spec 16.3). **`q` ist Pflicht und nicht optional**, und
  // zwar leer statt fehlend: Die Anfrage IST der Zustand dieser Seite, und ein
  // `undefined` daneben waere ein zweiter Ausdruck fuer "nichts gesucht". Sie
  // steht im Klartext in der Adresse (`?q=`), damit ein Mensch sie in einer
  // E-Mail lesen kann -- der Link traegt eine Frage, keine Antwort.
  | { readonly name: 'suche'; readonly q: string }
  | { readonly name: 'journal' }
  | { readonly name: 'entry'; readonly id: string }
  | { readonly name: 'periods' }
  | { readonly name: 'accounts' }
  // Sachkonten und Abgleich — das Kontoblatt (`V-kontoblatt`), die
  // GoBD-Kontenfunktion. `number` und nicht `id`: Das Blatt läuft über die
  // Kontonummer, nicht über eine Versions-id (dieselbe Abgrenzung wie
  // `KontoblattQueryService`). `from`/`to` sind eine Vorauswahl wie bei
  // `bank-account` oben — derselbe Grund, dieselbe Bauart.
  | { readonly name: 'account-ledger'; readonly number: string; readonly from?: string; readonly to?: string }
  // Die persönliche Profilseite (Auftrag `W-profilseite`, 2026-08-31) — Kopf,
  // Passwortwechsel (bis dahin unter `/passwort`, eigener Kopfzeilenknopf)
  // und die eigenen letzten Anmeldevorgänge. Erreichbar über die
  // Sitzungsecke unten links in der Menüspalte (`Sidebar.tsx`).
  | { readonly name: 'profile' }
  // Phase 2 — die Stammdaten.
  | { readonly name: 'customers' }
  | { readonly name: 'customer'; readonly id: string }
  | { readonly name: 'customer-new' }
  | { readonly name: 'duplicates' }
  | { readonly name: 'vendors' }
  | { readonly name: 'vendor'; readonly id: string }
  | { readonly name: 'vendor-new' }
  // Phase 3 — das Belegwesen.
  // **`tab` ist die Schnellfilterleiste der Belegliste** (Auftrag
  // `W-belegtabs`) — dasselbe Muster wie `konto` bei `klaerliste` unten:
  // fehlend heisst „Alle", ein Wert steht im Klartext in der Adresse, damit
  // sich ein gefilterter Blick teilen und mit F5 neu laden lässt. Geschrieben
  // wird er über `Navigation.replace` und nicht `navigate` — ein Tabwechsel
  // ist eine Filteränderung auf derselben Seite, kein Seitenwechsel.
  | { readonly name: 'documents'; readonly tab?: DocumentTab }
  | { readonly name: 'document'; readonly id: string }
  | { readonly name: 'document-new' }
  | { readonly name: 'number-ranges' }
  | { readonly name: 'issuer' }
  // Die E-Mail-Vorlagen (`F-mailmaske`) — **eine** Adresse und nicht drei wie
  // bei den Aborechnungen darunter. Eine Vorlage hat keine eigene Seite: Ihre
  // Bearbeitung sitzt im Dialog, ihre Historie im Seitenpanel, und beide
  // gehören zu einem Zustand der Liste und nicht zu einem eigenen Ort. Eine
  // weitergebbare Adresse je Vorlage wäre erst dann berechtigt, wenn es dort
  // etwas gäbe, das man jemandem schickt.
  | { readonly name: 'mailvorlagen' }
  // Die Belegvorlage (`BV-nullseite`) — wie die E-Mail-Vorlagen **eine** Adresse:
  // Reiter, Sprache und Fassungsliste sind Zustände der Seite und keine Orte, die man
  // jemandem schickt.
  | { readonly name: 'belegvorlagen' }
  // Wiederkehrende Rechnungen — die drei Adressen der Vorlagen
  // (`F-aboliste`). Dieselbe Bauart wie `documents`/`document-new`/`document`
  // eine Zeile darueber, und aus demselben Grund: Eine Vorlage hat unter
  // `/aborechnungen/<id>` eine eigene, weitergebbare Adresse, und die Anlage
  // eine eigene Maske statt eines Dialogs — sie traegt Kopf UND Positionen,
  // und das passt in keinen Dialog neben einer Liste.
  | { readonly name: 'aborechnungen' }
  | { readonly name: 'aborechnung'; readonly id: string }
  | { readonly name: 'aborechnung-new' }
  // Die Lastschriftdateien (`LS-einzug`, Spec 5, `D-16`) — eine Adresse, kein
  // Reiter: Der Reiter „Lastschrift" unter Belege zeigt die FÄLLIGEN Belege
  // (`DocumentsPage.tsx`, `DOCUMENT_TABS`), diese Adresse zeigt das ARCHIV der
  // erzeugten Dateien samt ihrer Einreichung — ein eigenständiger Bestand,
  // keine Ableitung eines Belegfilters.
  | { readonly name: 'lastschrift' }
  // Bankanbindung Stufe 1 — die vier Ansichten aus Spec 10.
  //
  // **`neu` ist ein Signal an die Seite, keine eigene Adresse.** Ein Geldkonto
  // entsteht über den Dialog DER LISTE (Design-Annäherung Spec 7.3) — es gibt
  // kein `/bankkonten/neu`, absichtlich, siehe die Begründung bei
  // `bank-account` unten. Der Kopfzeilenknopf «Neu» kann aber nur Adressen
  // anspringen (`navigate(route)`); dieses Feld ist die Naht, über die er der
  // schon offenen Liste trotzdem sagen kann „öffne den Dialog jetzt" —
  // `?neu=1` in der Adresse, gelesen von `BankAccountsPage.tsx` beim Aufbau.
  | { readonly name: 'bank-accounts'; readonly neu?: boolean }
  // **`from`/`to` sind eine Vorauswahl für die Umsatzliste, keine eigene
  // Adresse.** Ein Verweis aus der Auszugsliste trägt den Zeitraum des
  // Auszugs in der Adresszeile mit (`?from=...&to=...`); ohne Angabe verhält
  // sich die Seite wie zuvor. Beide sind `YYYY-MM-DD`-Zeichenketten, keine
  // `Date`-Objekte — dieselbe Form wie die Eingabefelder der Seite selbst.
  | { readonly name: 'bank-account'; readonly id: string; readonly from?: string; readonly to?: string }
  | { readonly name: 'bank-import'; readonly id: string }
  | { readonly name: 'bank-statements'; readonly id: string }
  // Sachkonten und Abgleich Stufe A — die Kontierungsmaske (`V-kontmaske`,
  // Task 9). Steht unter demselben dritten Wegstück wie `einlesen`/
  // `auszuege`/`abruf`/`klaerliste` oben: ein Geldkonto bestimmt die
  // unkontierten Bewegungen, dieselbe Bauart wie bei den Zahldiensten.
  // **`bewegungId` ist eine eigene, direkt anspringbare Adresse und kein
  // Anzeigesignal wie `neu` oben.** `/bankkonten/:id/kontieren/:bewegungId`
  // öffnet die Kontierungsmaske für GENAU diese Bewegung — derselbe Zweck wie
  // `/belege/:id`: Eine zweite Person, die eine bereits kontierte (aber noch
  // nicht freigegebene) Bewegung prüfen soll, findet sie nicht mehr in der
  // Unkontiert-Liste (`unkontiert.query.ts`) und braucht eine Adresse, die
  // die Bewegung selbst trägt.
  | { readonly name: 'kontieren'; readonly id: string; readonly bewegungId?: string }
  // Zahldienste Stufe A — die zwei Unterseiten, die es beim Bankweg nicht gibt.
  //
  // **Die Kontoansicht selbst bekommt KEINE eigene Adresse.** Ein
  // Zahldienstkonto IST ein Geldkonto (Spec Zahldienste 13, Punkt 1); eine
  // zweite Adresse für dieselbe Sache behauptete einen Unterschied, den das
  // Datenmodell nicht macht — und `/bankkonten/<id>` müsste für ein
  // PayPal-Konto dann etwas Falsches zeigen. Welche der beiden Ansichten unter
  // dieser Adresse erscheint, entscheidet der `provider` des geladenen Kontos.
  | { readonly name: 'zahldienst-abruf'; readonly id: string }
  | { readonly name: 'zahldienst-klaerliste'; readonly id: string }
  // Sachkonten und Abgleich Stufe A — die kontenübergreifende Klärliste
  // (`V-klaerliste4`, Task 10, Spec 4.5). Eigene, kontounabhängige Adresse
  // **neben** `zahldienst-klaerliste` oben, die bestehen bleibt: Ein Verweis
  // von einer Kontoseite («Kontenabgleich» im Menü der Kontoansicht) trägt weiter
  // die Kontokennung, ein Verweis aus dem Hauptmenü keine. Beide zeigen
  // dieselbe Komponente — `konto` ist dort nur die anfängliche Filterwahl,
  // keine zweite Seite. `konto` fehlt heisst «Alle Konten» (`A-K4`, bis
  // `KO-5` beantwortet ist).
  | { readonly name: 'klaerliste'; readonly konto?: string }
  // Zahlungszuordnung Stufe 1 — die Klärungsliste der Geldeingänge ohne
  // Zuordnung (`ZZ-web`, Task 7, Spec 9). **Eine eigene Adresse neben
  // `klaerliste` und ausdrücklich nicht dieselbe** (`D-26`): Jene beantwortet
  // «welches Sachkonto», diese «welche Rechnung». Zwei Fragen in einer Maske
  // sind eine Maske, die keine der beiden gut stellt.
  //
  // **Ohne Filter in der Adresse.** Der Klärgrund ist ein Zustand der Seite
  // und keiner, den man jemandem schickt — anders als `konto` bei
  // `klaerliste`, das aus einem Verweis der Kontoansicht stammt. Kommt
  // später ein teilbarer Blick dazu, ist ein Suchparameter dieselbe additive
  // Erweiterung, die `from`/`to` bei `bank-account` war.
  | { readonly name: 'zahlungsklaerung' }
  // **Seit `ZZ-freigabeliste` (2026-09-06).** Die Freigabeliste der
  // Zahlungszuordnungen — `zahlung:freigeben`, und daneben der
  // Guthaben-Handweg (`D-28`) für den, der `zahlung:zuordnen` UND
  // `guthaben:einloesen` trägt. Eigene Adresse und kein Abschnitt der
  // Klärungsliste: Jene zeigt Bewegungen OHNE Zuordnung, diese Zuordnungen
  // ohne Freigabe — die Sicht `zahlung_klaerung` (`0161`) führt eine
  // bereits zugeordnete Bewegung ausdrücklich nicht mehr, und ein Abschnitt
  // dort behauptete eine Zugehörigkeit, die die Daten nicht haben.
  //
  // **Ohne Filter in der Adresse**, aus demselben Grund wie eine Zeile
  // darüber: Der Quellenfilter ist ein Zustand der Seite und keiner, den man
  // jemandem schickt.
  | { readonly name: 'zahlungsfreigabe' }
  // Eingangsrechnungen Stufe A — die drei Ansichten aus Chunk 7.
  //
  // **`eingang` und nicht `eingangsrechnungen`.** Der Korb enthält
  // Archivstücke, von denen sich erst beim Erfassen herausstellt, ob sie
  // überhaupt Rechnungen sind (`not_an_invoice` ist einer der drei
  // Verwerfensgründe). Eine Adresse, die es behauptet, wäre an genau den
  // Stücken falsch, wegen deren es den Grund gibt.
  // **`neu` ist dasselbe Signal wie bei `bank-accounts` oben, für dieselbe
  // Naht.** «Belege hochladen» hat keine eigene Adresse — die Ablagefläche
  // steht auf dieser Seite (Design-Annäherung Spec 7.3, Task `V-eingang`).
  // `?neu=1` sagt `EingangskorbPage.tsx`, die Ablagefläche beim Aufbau in den
  // Blick zu rücken.
  | { readonly name: 'eingang'; readonly neu?: boolean }
  // **Die Kennung ist `null`, wenn ohne Archivstück erfasst wird** — der
  // Papierweg, bei dem der Beleg vor dem Scan entsteht. Zwei Adressen dafür
  // wären zwei Masken, die auseinanderlaufen.
  //
  // **`invoiceId` kam am 2026-08-22 dazu, zum Wiederöffnen eines bereits
  // erfassten Belegs.** `id` bleibt die Kennung eines ARCHIVSTÜCKS — die
  // Maske startet dann leer und legt beim Erfassen einen neuen Beleg an.
  // `invoiceId` ist die Kennung eines vorhandenen BELEGS — die Maske lädt ihn
  // über `GET /incoming-invoices/:id` vollständig und startet nicht leer.
  // Beide zugleich gäbe es nicht: Ein Archivstück ohne Beleg hat keinen
  // Beleg, den man öffnen könnte, und ein erfasster Beleg braucht die
  // Archivkennung nicht mehr — er trägt seinen eigenen Verweis auf die
  // Archivstücke in der Antwort.
  | { readonly name: 'eingang-erfassen'; readonly id: string | null; readonly invoiceId?: string }
  | { readonly name: 'kreditorenerwartung' }
  // Sachkonten und Abgleich — die Guthabenabstimmung im Webinterface,
  // Auftrag `W-abstimmansicht`. NUR LESEND: Der Lauf, der einen Satz
  // schreibt, bleibt `pnpm guthaben:abstimmung` (CLI); diese beiden Adressen
  // zeigen seine Protokolle. Liste und Detail wie bei `customers`/`customer`.
  | { readonly name: 'guthaben-abstimmung' }
  | { readonly name: 'guthaben-abstimmung-detail'; readonly id: string }
  // Guthaben-Erstattung — die Freigabeliste (Auftrag `V-guthabenauszahlung`,
  // Auftraggeberwunsch 2026-08-28). Die Anlage selbst hat KEINE eigene
  // Adresse: Sie entsteht über den Dialog am Kunden (`/kunden/:id`), nicht
  // über eine eigene Route — dieselbe Bauart wie „Konto anlegen" bei
  // `bank-accounts`.
  | { readonly name: 'guthaben-erstattungen' }
  // Guthaben-Korrekturen — die Liste und die Freigabe (Auftrag
  // `GU-korrekturmaske`, Auftraggeberfrage vom 2026-09-08). Eine Adresse und
  // keine zweite fuer die Einzelansicht: Die Kette einer Korrektur ist ein
  // Zustand ihrer Zeile und nichts, das man jemandem schickt — sie klappt in
  // der Liste auf, wie die Belege je Kunde auf `/offene-posten`. Die ANLAGE
  // hat aus demselben Grund wie bei den Erstattungen keine eigene Adresse; sie
  // entsteht heute ausschliesslich ueber den Cutover-Laeufer.
  | { readonly name: 'guthaben-korrekturen' }
  // DATEV-Export (Chip `DX-maske`, Task 13). EINE Adresse und keine vier:
  // Die vier Schritte der Spec (Periode waehlen, Vorpruefung, Erzeugen,
  // Freigeben) sind Abschnitte EINER Seite und keine vier Orte -- wer nach
  // dem Erzeugen auf eine andere Adresse spraenge, verloere den Bericht des
  // Trockenlaufs, an dem er gerade entschieden hat. Ein einzelner Stapel hat
  // keine eigene Adresse: Sein Register steht auf derselben Seite, und ein
  // tiefer Link auf eine Registerzeile haette keinen Empfaenger.
  | { readonly name: 'datev-export' }
  // Der Geldkonto-Anfangsbestand — Auftrag `K-geldkontenmaske` (2026-09-08).
  // Nur Lesen und Freigeben: Die Anlage bleibt ausschliesslich der Handlauf
  // `pnpm kontierung:geldkontenanfang -- --run`
  // (`GeldkontoAnfangsbestandController`, Kopfkommentar „KEIN `POST` ZUM
  // ANLEGEN"), keine eigene Adresse dafür. Eine Adresse ohne Parameter, wie
  // `guthaben-abstimmung`: Es gibt so viele Zeilen wie Geldkonten, keine
  // Detailansicht.
  | { readonly name: 'geldkonto-anfangsbestand' }
  // Die Saldenvorträge der Sachkonten — Auftrag `K-saldenvortrag`
  // (2026-10-06, `0285`, `D-53`). Eine Adresse ohne Parameter wie der Nachbar
  // darüber: Anlage und Freigabe stehen auf derselben Seite, es gibt höchstens
  // so viele Zeilen wie Bilanzkonten.
  | { readonly name: 'saldenvortraege' }
  // Sachkonten und Abgleich — die Offene-Posten-Liste je Kunde (Plan Task 14,
  // `C-offeneposten`). Eine eigene Adresse ohne Parameter: Die Liste ist
  // kontenübergreifend und kundenübergreifend, und ein Filter darauf gibt es
  // heute nicht. Der Zahlstatus JE Rechnung hat bewusst keine eigene Adresse —
  // er steht in der Belegliste und in `/belege/:id`, wo die Rechnung ohnehin
  // schon aufgeschlagen ist.
  | { readonly name: 'offene-posten' }
  // Die Auswertungen (Ära vom 2026-09-10, Spec Abschnitt 3.1, Plan Task 2
  // `[AW-geruest]`) — **acht Adressen auf einmal und nicht acht Chips lang
  // je eine.** `pnpm docs:current` liest die Adressen aus dieser Datei und
  // hält sie gegen `[ROUTENZAHL:n]` in `docs-site/content/architecture/operator-ui.md`;
  // acht Adressen über acht Aufgaben verteilt hiessen achtmal dieselbe Marke
  // bewegen und acht Gelegenheiten, sie beim Zusammenführen falsch zu
  // bewegen.
  //
  // **`auswertungen` ist die Bereichsseite und keine Weiterleitung.** Sie
  // trägt die Reiterleiste und nennt, welche Auswertung es gibt und aus
  // welcher Zeitschicht jede liest — ein `/auswertungen`, das sofort auf
  // `/auswertungen/umsatz` spränge, verlöre genau diese Übersicht und
  // machte den Zurück-Knopf des Browsers zu einer Falle.
  //
  // **Keine Parameter, auch nicht für Zeitraum und Reihenlänge.** Beide sind
  // heute ein Zustand der Seite; kommt ein teilbarer Blick dazu, ist ein
  // Suchparameter dieselbe additive Erweiterung, die `from`/`to` bei
  // `bank-account` war. Sieben eigene Adressen und keine EINE mit einem
  // `bereich`-Parameter: Jede Unterseite erklärt genau eine Zeitschicht
  // (Spec 3.1), und ein Verweis auf „die Forderungsauswertung" soll das
  // Wort tragen und nicht eine Nummer.
  //
  // **Neunte Adresse seit `BWA-grund` (2026-09-14):** `auswertung-bwa`, die
  // Betriebswirtschaftliche Auswertung (Spec
  // `docs/superpowers/specs/2026-09-14-bwa-design.md` Abschnitt 6) — sie
  // reiht sich als achte UNTERSEITE ein, aus derselben Zeitschicht-Regel
  // heraus (Journal ab dem Journalbeginn, mit der einen benannten Ausnahme der
  // Vorjahres-Umsatzzeile aus der Belegschicht).
  //
  // **Zehnte Adresse seit `UV-web` (2026-10-10):** `auswertung-ustva`, das
  // Kennzahlblatt der Umsatzsteuer-Voranmeldung (Spec
  // `docs/superpowers/specs/2026-10-09-ustva-kennzahlblatt-design.md`
  // Abschnitt 5.1) — neunte Unterseite, Journal ab dem Journalbeginn.
  | { readonly name: 'auswertungen' }
  | { readonly name: 'auswertung-umsatz' }
  | { readonly name: 'auswertung-kunden' }
  | { readonly name: 'auswertung-zahlungswege' }
  | { readonly name: 'auswertung-forderungen' }
  | { readonly name: 'auswertung-kosten' }
  | { readonly name: 'auswertung-steuer' }
  | { readonly name: 'auswertung-prognose' }
  | { readonly name: 'auswertung-bwa' }
  | { readonly name: 'auswertung-ustva' }
  // Betrieb — der Systemstatus. Steht am Ende der Aufzaehlung, weil er keine
  // Fachansicht ist: Er zeigt, ob die Fachansichten arbeiten koennen.
  | { readonly name: 'system' }
  // Die Benutzerverwaltung (`W-mitarbeiteranlage`). Wie `system` keine
  // Fachansicht, sondern Betrieb — deshalb direkt daneben und ohne
  // Unterrouten: Anlegen laeuft ueber einen Dialog auf derselben Seite
  // (dieselbe Bauart wie `bank-accounts`), es gibt keine Detailadresse je
  // Benutzer.
  | { readonly name: 'users' }
  // Die Tokenverwaltung (`W-apitoken-ui`). Dritter Betriebspunkt neben
  // «Benutzer» und «Systemstatus», aus demselben Grund wie diese und mit
  // derselben Bauart: keine Unterrouten, kein Anlegen ueber eine eigene
  // Adresse — ein Token entsteht im Dialog der Liste, weil sein Wert genau
  // einmal erscheint und eine Adresse ihn in den Verlauf des Browsers traege.
  | { readonly name: 'api-tokens' }
  // Die Lizenzseite (`LZ-erneuerung`, Spec Lizenzierung 8). Betrieb wie
  // «API-Tokens», eigene Faehigkeit `lizenz:verwalten` (allein `admin`). Keine
  // Unterrouten: Hinterlegen, Jetzt pruefen und Neuanmeldung sind Handlungen
  // auf derselben Seite. Verabredet ist sie als Abschnitt der
  // Einstellungsseite (Aera PF, `D-48`); bis es die gibt, eine eigene Adresse.
  | { readonly name: 'lizenz' }
  // Die Datensicherung (`DS-web`, Spec Datensicherung 10, `D-25`). Betrieb wie
  // «Systemstatus», Faehigkeiten `sicherung:read` und `sicherung:manage`. Keine
  // Unterrouten: Die Einrichtung ist eine Folge von Schritten auf derselben Seite,
  // und ein Schritt traegt Geheimnisse, die keine Adresse tragen darf.
  | { readonly name: 'sicherung' }
  // Das Regelregister, NUR LESEND (`W-regeluebersicht`, 2026-08-31, Stufe 1
  // des Regel-UI-Wunschs). Wie `system` Betrieb und keine Fachansicht: Sie
  // zeigt, welche Regeln es gibt und seit wann — nicht, was sie gebucht
  // haben (das bleibt `kontierungWartend`/`/klaerliste`). Keine Unterrouten,
  // kein Anlegen: Regeln sind Code (`REI-2 = B`).
  | { readonly name: 'regeln' }
  // Der Anfrage-Debugger (`W-debugger-ui`, 2026-09-01). Wie `system` Betrieb
  // und keine Fachansicht — dieselbe Fähigkeit `system:read`, direkt neben dem
  // Systemstatus. **Keine Unterroute je Anfrage:** Die Detailansicht ist ein
  // `SidePanel` über der Liste und keine eigene Seite; eine Adresse
  // `/anfragen/<id>` verspräche einen teilbaren Verweis auf eine Protokollzeile,
  // deren Kennung ausserhalb dieses Rechners nichts bedeutet.
  | { readonly name: 'anfragen' }
  // Die Webhook-Verwaltung (`WH-oberflaeche`, 2026-09-10). Wie `system`
  // Betrieb und keine Fachansicht -- sie sagt, wohin dieses Haus Nachrichten
  // schickt, und nicht, was gebucht wurde. Eigene Faehigkeit
  // `webhooks:manage` (`D-8`), die allein der Administrator traegt.
  //
  // KEINE UNTERROUTE JE ENDPUNKT: Das Zustellungsprotokoll ist ein Bereich
  // UNTER der Liste und keine eigene Seite. Ein Betrieb hat ein bis zwei
  // Empfaenger; eine Adresse `/webhooks/<id>/zustellungen` verspraeche einen
  // teilbaren Verweis auf eine Protokollansicht, deren Kennung ausserhalb
  // dieses Rechners nichts bedeutet -- dieselbe Erwaegung wie bei `anfragen`.
  | { readonly name: 'webhooks' }
  // Die Arbeitsliste der Portal-Vorgaenge (`KP-oberflaeche`, 2026-09-17). Wie
  // `webhooks` Betrieb und keine Fachansicht -- sie sagt, was ein Kunde im
  // Kundenportal gefragt oder beantragt hat und noch auf einen Menschen
  // wartet. Faehigkeit `document:read` zum Lesen, `document:manage` zum
  // Erledigen (Spec Kundenportal Abschnitt 6, Betrieb-Tabelle) -- und
  // ausdruecklich NICHT `portal:zugriff`: Die traegt keine Rolle, sie gehoert
  // dem Portal-Token (`D-7`).
  //
  // KEINE UNTERROUTE JE VORGANG: Ein Vorgang ist eine Zeile mit Rueckfragetext
  // oder beantragten Feldern und einem Knopf daneben; eine Adresse
  // `/portal-vorgaenge/<id>` verspraeche einen teilbaren Verweis auf eine
  // Arbeitszeile, die nach dem Erledigen aus der Liste verschwindet --
  // dieselbe Erwaegung wie bei `anfragen` und `webhooks`.
  //
  // KEIN ZUSTAND IN DER ADRESSE: Offen und erledigt sind zwei Reiter derselben
  // Seite und keine zwei Adressen. Ein `?status=erledigt` im Verlauf des
  // Browsers saehe aus wie eine gespeicherte Auswahl und waere keine -- die
  // Seite oeffnet immer auf «offen», weil das die Frage ist, die sie
  // beantwortet.
  | { readonly name: 'portal-vorgaenge' }
  // Der Einrichtungsassistent (`PF-assistent`, 2026-09-30, Task 11 des Plans
  // Systemeinstellungen, Spec Abschnitt 6.5). **Eine Adresse und keine acht:**
  // Die acht Schritte sind Abschnitte EINER Seite — ein Schritt, der eine eigene
  // Adresse hätte, liesse sich per Lesezeichen anspringen, ohne dass die
  // Voraussetzungen davor erfüllt sind. Der Schritt ist ein Zustand der Seite.
  //
  // Bei offener Einrichtung führt die Schale jede andere Adresse hierher
  // (`einstellungen/einrichtungslage.ts`); nach dem Abschluss öffnet dieselbe
  // Seite im Änderungsmodus (Schritte 2, 3, 6, 7 einzeln, der Rest gesperrt
  // und als solcher beschriftet, `D-40`).
  | { readonly name: 'einrichtung' }
  // Die Seite „Übernahme aus Lexware Office" (`LX-webseite`, Task 33 des Plans
  // Lexware-Migration, Spec 20.2). **Eine Adresse und keine Unterrouten:** Die
  // Schritte sind Zeilen EINER Seite, der Fortschritt eines Auftrags ein Zustand
  // von ihr. Sie ist der zweite Ort neben dem Einrichtungsschritt „Vorsystem" und
  // gehört nach dem Abschluss der Einrichtung; die API verlangt für jede Route
  // `vorsystem:import`.
  | { readonly name: 'vorsystem' };

/**
 * Ein Suchparameterwert, sofern er wie `YYYY-MM-DD` aussieht — sonst
 * `undefined`.
 *
 * **Malformte Werte werden ignoriert, nicht abgelehnt.** Eine Adresse ist
 * Fremdeingabe (von Hand getippt, aus einem alten Lesezeichen, verstümmelt
 * beim Teilen); die Seite verhält sich dann wie ohne Vorauswahl, statt einen
 * Fehler zu zeigen.
 */
const DATUM_MUSTER = /^\d{4}-\d{2}-\d{2}$/;
function alsDatum(wert: string | null): string | undefined {
  return wert !== null && DATUM_MUSTER.test(wert) ? wert : undefined;
}

/**
 * Die Schnellfilterleiste der Belegliste — Auftrag `W-belegtabs`, seit
 * `LS-einzug` um einen sechsten Reiter erweitert.
 *
 * Sechs Reiter: „Alle" trägt keinen eigenen Wert (fehlend heisst Alle,
 * dasselbe Muster wie `konto` bei `klaerliste`). `entwuerfe`, `offen`,
 * `ueberfaellig`, `bezahlt` sind abgeleitete Zahlungszustände, keine
 * `document_status`-Werte — `entwuerfe` ist der einzige, der auch den
 * Belegzustand selbst trifft (`status === 'draft'`); die übrigen drei
 * filtern über den Zahlstand (`RechnungZahlstand`), den `DocumentsPage.tsx`
 * ohnehin schon je Seite lädt. **`lastschrift` ist KEINE Ableitung der
 * geladenen Belegseite** — er zeigt die Sicht `lastschrift_faellig` (Spec
 * 2.6, `D-3`), eine eigenständige Liste mit eigenen Spalten
 * (`FaelligTabelle.tsx`), nicht die gefilterte Belegtabelle.
 */
const DOCUMENT_TABS = ['entwuerfe', 'offen', 'ueberfaellig', 'bezahlt', 'lastschrift'] as const;
export type DocumentTab = (typeof DOCUMENT_TABS)[number];
function alsDocumentTab(wert: string | null): DocumentTab | undefined {
  return wert !== null && (DOCUMENT_TABS as readonly string[]).includes(wert)
    ? (wert as DocumentTab)
    : undefined;
}

/**
 * Die sieben Unterseiten der Auswertungen: Adressname → letztes Wegstück.
 *
 * **EINE Tabelle für beide Richtungen.** `toRoute` sucht in ihr das
 * Wegstück, `toPath` liest es — sieben Zeichenketten, die an zwei Orten
 * stünden, wären sieben Gelegenheiten, eine davon nur an einem zu ändern.
 * Der Rest dieser Datei schreibt beide Richtungen von Hand, weil dort jede
 * Adresse eine eigene Form hat (Parameter, Suchteile, Reihenfolgefragen);
 * diese sieben haben dieselbe Form und unterscheiden sich in genau einem
 * Wort.
 *
 * Das Wegstück ist zugleich das Wegstück des Endpunkts
 * (`GET /api/v1/auswertungen/<wegstueck>`) und der Schlüssel, den die
 * Bereichsseite ausliefert — ein Name und nicht drei.
 */
const AUSWERTUNG_WEGSTUECK = {
  'auswertung-umsatz': 'umsatz',
  'auswertung-kunden': 'kunden',
  'auswertung-zahlungswege': 'zahlungswege',
  'auswertung-forderungen': 'forderungen',
  'auswertung-kosten': 'kosten',
  'auswertung-steuer': 'steuer',
  'auswertung-prognose': 'prognose',
  // Achte Adresse seit `BWA-grund` (2026-09-14, Spec
  // `docs/superpowers/specs/2026-09-14-bwa-design.md` Abschnitt 6).
  'auswertung-bwa': 'bwa',
  // Zehnte Adresse und neunte Unterseite seit `UV-web` (2026-10-10): das
  // Kennzahlblatt der Umsatzsteuer-Voranmeldung (Spec
  // `docs/superpowers/specs/2026-10-09-ustva-kennzahlblatt-design.md`
  // Abschnitt 5.1).
  'auswertung-ustva': 'ustva',
} as const;

type AuswertungAdresse = keyof typeof AUSWERTUNG_WEGSTUECK;

const AUSWERTUNG_ADRESSEN = Object.keys(AUSWERTUNG_WEGSTUECK) as readonly AuswertungAdresse[];

/**
 * Die Adresse zu einem Wegstück — oder `undefined`, wenn es keines der
 * sieben ist.
 *
 * **Ein unbekanntes Wegstück landet auf der Bereichsseite und nicht im
 * Journal** (siehe Aufrufer). Eine Adresse ist Fremdeingabe: Wer
 * `/auswertungen/umsaetze` aus einem alten Lesezeichen aufruft, will
 * Auswertungen sehen und nicht das Journal.
 */
/**
 * Gehört diese Adresse zum Bereich «Auswertungen»?
 *
 * **Eine Frage an den Router und keine Liste in `App.tsx`.** Dort stünden
 * sonst acht Adressnamen ein zweites Mal, und eine neunte Auswertung wäre
 * an zwei Orten nachzutragen — an einem davon würde sie vergessen. Der
 * Router weiss ohnehin, welche Adressen es gibt.
 */
export function istAuswertungsadresse(route: Route): boolean {
  return route.name === 'auswertungen' || route.name in AUSWERTUNG_WEGSTUECK;
}

function auswertungAdresse(wegstueck: string): Route | undefined {
  const name = AUSWERTUNG_ADRESSEN.find((kandidat) => AUSWERTUNG_WEGSTUECK[kandidat] === wegstueck);
  return name === undefined ? undefined : { name };
}

/**
 * Übersetzt einen Pfad samt Suchteil in eine Adresse. Unbekanntes landet im
 * Journal.
 *
 * **`neu` steht vor dem Parameterzweig.** Stünde es danach, läse `/kunden/neu`
 * das Wort `neu` als Kennung, und die Maske lüde einen Kunden, den es nicht
 * gibt. Dieselbe Reihenfolgefrage wie bei `GET /customers/duplicates` im
 * Controller — dort fängt der Parameter sonst das Wort `duplicates` ab.
 *
 * **`search` ist optional und bewusst zweiter Parameter.** Nur die Kontoansicht
 * liest bisher etwas daraus (`from`/`to`); jeder andere Aufruf lässt ihn weg
 * und bekommt dieselbe Adresse wie vorher.
 */
export function toRoute(pathname: string, search = ''): Route {
  const parts = pathname.split('/').filter((part) => part !== '');
  const [first, second, third, fourth] = parts;

  if (first === 'uebersicht') return { name: 'overview' };
  // `q` wird nicht geprueft und nie abgelehnt: Eine Adresse ist Fremdeingabe,
  // und was die Anfrage bedeutet, entscheidet ohnehin der Server (Spec 4.1).
  if (first === 'suche') return { name: 'suche', q: new URLSearchParams(search).get('q') ?? '' };
  if (first === 'perioden') return { name: 'periods' };
  if (first === 'konten') {
    // `blatt` steht im DRITTEN Wegstück, wie `einlesen`/`auszuege` bei
    // `bankkonten` — eine Kontonummer kann `blatt` nicht abfangen, weil sie
    // an zweiter Stelle steht.
    if (second !== undefined && third === 'blatt') {
      const params = new URLSearchParams(search);
      const from = alsDatum(params.get('from'));
      const to = alsDatum(params.get('to'));
      return {
        name: 'account-ledger',
        number: second,
        ...(from === undefined ? {} : { from }),
        ...(to === undefined ? {} : { to }),
      };
    }
    return { name: 'accounts' };
  }
  if (first === 'profil') return { name: 'profile' };
  if (first === 'dubletten') return { name: 'duplicates' };

  if (first === 'kunden') {
    if (second === undefined) return { name: 'customers' };
    if (second === 'neu') return { name: 'customer-new' };
    return { name: 'customer', id: second };
  }

  if (first === 'kreditoren') {
    if (second === undefined) return { name: 'vendors' };
    if (second === 'neu') return { name: 'vendor-new' };
    return { name: 'vendor', id: second };
  }

  if (first === 'auswertungen') {
    if (second === undefined) return { name: 'auswertungen' };
    return auswertungAdresse(second) ?? { name: 'auswertungen' };
  }

  if (first === 'offene-posten') return { name: 'offene-posten' };
  if (first === 'nummernkreise') return { name: 'number-ranges' };
  if (first === 'kreditorenerwartung') return { name: 'kreditorenerwartung' };
  if (first === 'systemstatus') return { name: 'system' };
  if (first === 'benutzer') return { name: 'users' };
  if (first === 'api-tokens') return { name: 'api-tokens' };
  if (first === 'lizenz') return { name: 'lizenz' };
  if (first === 'datensicherung') return { name: 'sicherung' };
  if (first === 'regeln') return { name: 'regeln' };
  if (first === 'anfragen') return { name: 'anfragen' };
  if (first === 'webhooks') return { name: 'webhooks' };
  if (first === 'portal-vorgaenge') return { name: 'portal-vorgaenge' };
  if (first === 'einrichtung') return { name: 'einrichtung' };
  if (first === 'vorsystem') return { name: 'vorsystem' };

  // Guthabenabstimmung, NUR LESEND (`W-abstimmansicht`) — Liste unter
  // `/guthaben/abstimmung`, ein Satz unter `/guthaben/abstimmung/<id>`.
  if (first === 'guthaben' && second === 'abstimmung') {
    return third === undefined
      ? { name: 'guthaben-abstimmung' }
      : { name: 'guthaben-abstimmung-detail', id: third };
  }
  if (first === 'guthaben' && second === 'erstattungen') {
    return { name: 'guthaben-erstattungen' };
  }
  if (first === 'guthaben' && second === 'korrekturen') {
    return { name: 'guthaben-korrekturen' };
  }
  if (first === 'datev-export') return { name: 'datev-export' };
  if (first === 'geldkonten' && second === 'anfangsbestand') {
    return { name: 'geldkonto-anfangsbestand' };
  }
  if (first === 'saldenvortraege' && second === undefined) return { name: 'saldenvortraege' };

  // `V-klaerliste4` (Task 10) — die kontenübergreifende Klärliste, neben
  // `/bankkonten/:id/klaerliste` weiter unten. `konto` ist ein Suchparameter
  // wie `from`/`to` bei `bank-account`, keine Pflichtangabe.
  // `zahlungen` VOR `klaerliste` ist keine Reihenfolgefrage — die zwei Pfade
  // teilen kein Wegstück; die Nähe hier ist nur die inhaltliche.
  if (first === 'zahlungen' && second === 'klaerung') return { name: 'zahlungsklaerung' };
  if (first === 'zahlungen' && second === 'freigabe') return { name: 'zahlungsfreigabe' };

  if (first === 'klaerliste') {
    const konto = new URLSearchParams(search).get('konto');
    return { name: 'klaerliste', ...(konto === null || konto === '' ? {} : { konto }) };
  }

  if (first === 'eingang') {
    if (second === undefined) {
      const params = new URLSearchParams(search);
      return params.get('neu') === '1' ? { name: 'eingang', neu: true } : { name: 'eingang' };
    }
    // `erfassen` VOR jedem Parameterzweig — es gibt hier zwar keinen, und
    // genau deshalb steht der Hinweis da: Wer später `/eingang/<id>` ergänzt,
    // setzt es HINTER diese Zeile.
    if (second === 'erfassen') {
      // `beleg` VOR dem Archivstück-Parameterzweig, aus demselben Grund wie
      // `neu` bei `/kunden/neu`: Ohne diese Reihenfolge läse
      // `/eingang/erfassen/beleg` das Wort `beleg` als Archivstück-Kennung.
      if (third === 'beleg' && fourth !== undefined) {
        return { name: 'eingang-erfassen', id: null, invoiceId: fourth };
      }
      return { name: 'eingang-erfassen', id: third ?? null };
    }
    return { name: 'eingang' };
  }
  if (first === 'aussteller') return { name: 'issuer' };
  if (first === 'mailvorlagen') return { name: 'mailvorlagen' };
  if (first === 'belegvorlagen') return { name: 'belegvorlagen' };

  if (first === 'belege') {
    if (second === undefined) {
      const tab = alsDocumentTab(new URLSearchParams(search).get('tab'));
      return { name: 'documents', ...(tab === undefined ? {} : { tab }) };
    }
    // `neu` VOR dem Parameterzweig — sonst läse `/belege/neu` das Wort `neu`
    // als Kennung und die Maske lüde einen Beleg, den es nicht gibt.
    if (second === 'neu') return { name: 'document-new' };
    return { name: 'document', id: second };
  }

  if (first === 'lastschrift') return { name: 'lastschrift' };

  if (first === 'aborechnungen') {
    if (second === undefined) return { name: 'aborechnungen' };
    // `neu` VOR dem Parameterzweig — sonst läse `/aborechnungen/neu` das Wort
    // `neu` als Kennung und die Maske lüde eine Vorlage, die es nicht gibt.
    // Dieselbe Reihenfolgefrage wie bei `/kunden/neu` und `/belege/neu`.
    if (second === 'neu') return { name: 'aborechnung-new' };
    return { name: 'aborechnung', id: second };
  }

  if (first === 'bankkonten') {
    if (second === undefined) {
      const params = new URLSearchParams(search);
      return params.get('neu') === '1' ? { name: 'bank-accounts', neu: true } : { name: 'bank-accounts' };
    }
    // **Die Unterseiten stehen im DRITTEN Wegstück und nicht im zweiten.** Damit
    // stellt sich die Reihenfolgefrage von `/kunden/neu` hier gar nicht: Ein
    // Kontoschlüssel steht immer an zweiter Stelle, und `einlesen` kann ihn nicht
    // abfangen. Ein `/bankkonten/neu` gibt es nicht — ein Konto wird über einen
    // Dialog der Liste angelegt.
    if (third === 'einlesen') return { name: 'bank-import', id: second };
    if (third === 'auszuege') return { name: 'bank-statements', id: second };
    if (third === 'abruf') return { name: 'zahldienst-abruf', id: second };
    if (third === 'klaerliste') return { name: 'zahldienst-klaerliste', id: second };
    if (third === 'kontieren') {
      return {
        name: 'kontieren',
        id: second,
        ...(fourth === undefined ? {} : { bewegungId: fourth }),
      };
    }
    const params = new URLSearchParams(search);
    const from = alsDatum(params.get('from'));
    const to = alsDatum(params.get('to'));
    return {
      name: 'bank-account',
      id: second,
      ...(from === undefined ? {} : { from }),
      ...(to === undefined ? {} : { to }),
    };
  }

  if (first === 'journal' && second !== undefined) return { name: 'entry', id: second };
  return { name: 'journal' };
}

/** Der Pfad zu einer Adresse. Die Gegenrichtung von `toRoute`. */
export function toPath(route: Route): string {
  switch (route.name) {
    case 'overview':
      return '/uebersicht';
    case 'suche': {
      // `URLSearchParams` und keine eigene Kodierung: Der Klartext bleibt so
      // lesbar, wie die Adresszeile es zulaesst (`kreditor%3Am%C3%BCller+…`),
      // und eine leere Anfrage hinterlaesst KEIN `?q=` -- ein Fragezeichen
      // ohne Frage saehe im Verlauf des Browsers aus wie eine gespeicherte
      // Suche und waere keine.
      if (route.q.trim() === '') return '/suche';
      const params = new URLSearchParams();
      params.set('q', route.q);
      return `/suche?${params.toString()}`;
    }
    case 'entry':
      return `/journal/${route.id}`;
    case 'periods':
      return '/perioden';
    case 'accounts':
      return '/konten';
    case 'account-ledger': {
      const params = new URLSearchParams();
      if (route.from !== undefined) params.set('from', route.from);
      if (route.to !== undefined) params.set('to', route.to);
      const query = params.toString();
      return `/konten/${route.number}/blatt${query === '' ? '' : `?${query}`}`;
    }
    case 'profile':
      return '/profil';
    case 'customers':
      return '/kunden';
    case 'customer':
      return `/kunden/${route.id}`;
    case 'customer-new':
      return '/kunden/neu';
    case 'duplicates':
      return '/dubletten';
    case 'vendors':
      return '/kreditoren';
    case 'vendor':
      return `/kreditoren/${route.id}`;
    case 'vendor-new':
      return '/kreditoren/neu';
    case 'documents':
      return route.tab === undefined ? '/belege' : `/belege?tab=${route.tab}`;
    case 'document':
      return `/belege/${route.id}`;
    case 'document-new':
      return '/belege/neu';
    case 'aborechnungen':
      return '/aborechnungen';
    case 'aborechnung':
      return `/aborechnungen/${route.id}`;
    case 'aborechnung-new':
      return '/aborechnungen/neu';
    case 'lastschrift':
      return '/lastschrift';
    case 'number-ranges':
      return '/nummernkreise';
    case 'issuer':
      return '/aussteller';
    case 'mailvorlagen':
      return '/mailvorlagen';
    case 'belegvorlagen':
      return '/belegvorlagen';
    case 'bank-accounts':
      return route.neu === true ? '/bankkonten?neu=1' : '/bankkonten';
    case 'bank-account': {
      // `URLSearchParams` statt Zeichenkettenverkettung — auch, damit ein
      // `von`/`bis` mit einem Sonderzeichen (theoretisch, bei `YYYY-MM-DD`
      // nie) nicht als Adressbestandteil zerbräche.
      const params = new URLSearchParams();
      if (route.from !== undefined) params.set('from', route.from);
      if (route.to !== undefined) params.set('to', route.to);
      const query = params.toString();
      return `/bankkonten/${route.id}${query === '' ? '' : `?${query}`}`;
    }
    case 'bank-import':
      return `/bankkonten/${route.id}/einlesen`;
    case 'bank-statements':
      return `/bankkonten/${route.id}/auszuege`;
    case 'zahldienst-abruf':
      return `/bankkonten/${route.id}/abruf`;
    case 'zahldienst-klaerliste':
      return `/bankkonten/${route.id}/klaerliste`;
    case 'klaerliste': {
      const params = new URLSearchParams();
      if (route.konto !== undefined) params.set('konto', route.konto);
      const query = params.toString();
      return `/klaerliste${query === '' ? '' : `?${query}`}`;
    }
    case 'zahlungsklaerung':
      return '/zahlungen/klaerung';
    case 'zahlungsfreigabe':
      return '/zahlungen/freigabe';
    case 'kontieren':
      return route.bewegungId === undefined
        ? `/bankkonten/${route.id}/kontieren`
        : `/bankkonten/${route.id}/kontieren/${route.bewegungId}`;
    case 'eingang':
      return route.neu === true ? '/eingang?neu=1' : '/eingang';
    case 'eingang-erfassen':
      if (route.invoiceId !== undefined) return `/eingang/erfassen/beleg/${route.invoiceId}`;
      return route.id === null ? '/eingang/erfassen' : `/eingang/erfassen/${route.id}`;
    case 'kreditorenerwartung':
      return '/kreditorenerwartung';
    case 'guthaben-abstimmung':
      return '/guthaben/abstimmung';
    case 'guthaben-abstimmung-detail':
      return `/guthaben/abstimmung/${route.id}`;
    case 'guthaben-erstattungen':
      return '/guthaben/erstattungen';
    case 'guthaben-korrekturen':
      return '/guthaben/korrekturen';
    case 'datev-export':
      return '/datev-export';
    case 'geldkonto-anfangsbestand':
      return '/geldkonten/anfangsbestand';
    case 'saldenvortraege':
      return '/saldenvortraege';
    case 'auswertungen':
      return '/auswertungen';
    // Acht Fälle, eine Zeile: Die Adressen unterscheiden sich in genau
    // einem Wort, und das steht in `AUSWERTUNG_WEGSTUECK`. Die
    // Vollständigkeitsprüfung dieses `switch` bleibt trotzdem scharf — eine
    // neunte Auswertung ohne Zeile hier ist ein Übersetzungsfehler.
    case 'auswertung-umsatz':
    case 'auswertung-kunden':
    case 'auswertung-zahlungswege':
    case 'auswertung-forderungen':
    case 'auswertung-kosten':
    case 'auswertung-steuer':
    case 'auswertung-prognose':
    case 'auswertung-bwa':
    case 'auswertung-ustva':
      return `/auswertungen/${AUSWERTUNG_WEGSTUECK[route.name]}`;
    case 'offene-posten':
      return '/offene-posten';
    case 'system':
      return '/systemstatus';
    case 'users':
      return '/benutzer';
    case 'api-tokens':
      return '/api-tokens';
    case 'lizenz':
      return '/lizenz';
    case 'sicherung':
      return '/datensicherung';
    case 'regeln':
      return '/regeln';
    case 'anfragen':
      return '/anfragen';
    case 'webhooks':
      return '/webhooks';
    case 'portal-vorgaenge':
      return '/portal-vorgaenge';
    case 'einrichtung':
      return '/einrichtung';
    case 'vorsystem':
      return '/vorsystem';
    case 'journal':
      return '/journal';
  }
}

/** Die aktuelle Adresse samt Umschalter. */
export interface Navigation {
  readonly route: Route;
  readonly navigate: (route: Route) => void;
  /**
   * Wie `navigate`, aber ohne einen neuen Verlaufseintrag anzulegen
   * (`history.replaceState`).
   *
   * **Für Filteränderungen an derselben Seite, nicht für einen Seitenwechsel.**
   * Ein Filter, den man tippt, soll die Adresse tragen — reload und Teilen
   * behalten ihn dann —, aber nicht bei jedem Tastendruck einen eigenen
   * „Zurück"-Schritt erzeugen. Ein echter Seitenwechsel (Menü, ein Verweis wie
   * «Zu den Umsätzen») bleibt `navigate`.
   */
  readonly replace: (route: Route) => void;
}

/** Trennt eine `pathname`+`search`-Zeichenkette wieder in ihre zwei Teile. */
function teileAdresse(adresse: string): readonly [pathname: string, search: string] {
  const index = adresse.indexOf('?');
  return index === -1 ? [adresse, ''] : [adresse.slice(0, index), adresse.slice(index)];
}

/**
 * Liest die Adresse und hält sie mit der Verlaufsleiste im Gleichschritt.
 *
 * Der Zuhörer auf `popstate` ist der Grund, warum die Zurück-Taste des
 * Browsers funktioniert. Ohne ihn bliebe die Anzeige stehen, während die
 * Adresszeile zurückspringt — die häufigste Art, eine Einzelseitenanwendung
 * kaputtzumachen.
 *
 * **Der Zustand trägt jetzt Pfad UND Suchteil**, nicht mehr nur den Pfad —
 * sonst bliebe eine per `replace` geschriebene Filteränderung in der
 * Adresszeile stehen, ohne dass `route` sie je sähe.
 */
export function useNavigation(): Navigation {
  const [adresse, setAdresse] = useState(
    () => window.location.pathname + window.location.search,
  );

  useEffect(() => {
    const onPop = (): void => {
      setAdresse(window.location.pathname + window.location.search);
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  const navigate = useCallback((route: Route) => {
    const path = toPath(route);
    window.history.pushState(null, '', path);
    setAdresse(path);
  }, []);

  const replace = useCallback((route: Route) => {
    const path = toPath(route);
    window.history.replaceState(null, '', path);
    setAdresse(path);
  }, []);

  const [pathname, search] = teileAdresse(adresse);
  return { route: toRoute(pathname, search), navigate, replace };
}
